import React, { useEffect } from 'react';
import { Box, Text, TextLink } from '@indeed/ifl-components';
import { MarkupComponents } from '@indeed/frontend-markup-react-components';
import { JSMABridge } from '@indeed/jsma-js-bridge';
import { useProctorGroups } from '@indeed/frontend-proctor-utils';
import { useApplyForMeCopy } from '../hooks/useApplyForMeCopy';

/**
 * Auto Apply Terms Page
 * Displays the legal terms and conditions for Auto Apply in a mobile first layout
 */
export const AutoApplyTerms: React.FC = () => {
    const copy = useApplyForMeCopy();
    // Progressive automation (rnhp_auto_apply_banner_tst === 2) shows the same
    // Apply For Me (Beta) terms presented on the Review apply-for-me variant
    // (IAFE-2081), independent of the rebranding/automation-level proctors.
    const { getGroup } = useProctorGroups();
    const isProgressiveAutomation = getGroup('rnhp_auto_apply_banner_tst')?.value === 2;
    const termsHeading = isProgressiveAutomation
        ? copy.progressiveAutomationTermsHeading
        : copy.betaTermsHeading;
    const termsBody = isProgressiveAutomation
        ? copy.progressiveAutomationReviewTerms
        : copy.standaloneTermsBody;

    useEffect(() => {
        if (JSMABridge.isAvailable()) {
            void JSMABridge.setNavigationTitle({ title: termsHeading });
        }
    }, [termsHeading]);

    return (
        <Box
            as="main"
            sx={{
                maxInlineSize: '25.125rem',
                paddingInline: 5,
                marginInline: 'auto',
            }}
        >
            <Text
                level={3}
                sx={{
                    whiteSpace: 'pre-line',
                }}
            >
                <MarkupComponents
                    components={{
                        privacyPolicyLink: (children) => (
                            <TextLink
                                target="_blank"
                                href="https://hrtechprivacy.com/brands/indeed#privacypolicy"
                                inline
                                rel="noopener noreferrer"
                            >
                                {children}
                            </TextLink>
                        ),
                        termsOfServiceLink: (children) => (
                            <TextLink
                                target="_blank"
                                href="https://www.indeed.com/legal"
                                inline
                                rel="noopener noreferrer"
                            >
                                {children}
                            </TextLink>
                        ),
                    }}
                >
                    {termsBody}
                </MarkupComponents>
            </Text>
        </Box>
    );
};
